import { PositionsTable } from '@Components/Tables';
import { orderPositionColumns } from '@Constants/TableColumns';
import { useFeatureFlags } from '@Contexts/featureFlags';
import {
    type ApiContainerAction,
    type ApiOrder,
    type ApiOrderPosition,
    EnumFrontendLayoutSetting,
    type EnumProcessState,
    type EnumTaxationMode,
} from '@Model';
import {
    type AsyncStateUpdate,
    Badge,
    defaultIconSize,
    FlexColumn,
    FloatingCard,
    renderCurrency,
    type ToolbarOptions,
    useEditSubObject,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconClipboardList } from '@tabler/icons-react';
import { memo, useMemo } from 'react';
import { OrderPosition } from '../../OrderPosition';
import { positionOriginColumn } from '../../positionOriginColumn';
import classes from './OrderPositionsSection.module.css';

interface IProps {
    baseApiObjectPath: string;
    requestUpdateState: AsyncStateUpdate<ApiOrder>;
    stateQueueDepth: number;
    refetchData: () => Promise<boolean>;

    positions: ApiOrderPosition[];
    containerActions: ApiContainerAction[];
    taxationMode: EnumTaxationMode;
    processState: EnumProcessState;
    totalNetPrice: number;

    canEdit: boolean;
    canEditPositions: boolean;
}

// @ai-generated 2026-10-22T10:53:17+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export const OrderPositionsSection = memo(function OrderPositionsSection({
    baseApiObjectPath,
    requestUpdateState,
    stateQueueDepth,
    refetchData,
    positions,
    containerActions,
    taxationMode,
    processState,
    totalNetPrice,
    canEdit,
    canEditPositions,
}: IProps) {
    const i18n = useTranslate('');
    const { isFeatureEnabled } = useFeatureFlags();
    const showsPositionOrigin = isFeatureEnabled('CONTAINER_SERVICE');

    const {
        addSubObject: addPosition,
        deleteSubObjects: deletePositions,
        requestUpdateSubObject: requestUpdatePosition,
    } = useEditSubObject<ApiOrderPosition, ApiOrder>(baseApiObjectPath, 'positions', requestUpdateState);

    const positionColumns = useMemo(
        () =>
            showsPositionOrigin
                ? [...orderPositionColumns(taxationMode), positionOriginColumn(containerActions, positions)]
                : orderPositionColumns(taxationMode),
        [taxationMode, showsPositionOrigin, containerActions, positions],
    );

    const toolbarElements: ToolbarOptions[] = useMemo(
        () => [
            'add',
            'delete',
            <Badge key="totalPrice" variant="primary" type="input" size="sm">
                <IconClipboardList size={defaultIconSize} />
                &nbsp; Gesamtpreis: {renderCurrency(totalNetPrice)}
            </Badge>,
        ],
        [totalNetPrice],
    );

    return (
        <FloatingCard title={`${i18n('Positionen')} (${positions.length})`}>
            <FlexColumn align="stretch" className={classes.positionsScroll}>
                <PositionsTable<ApiOrderPosition>
                    layoutSettingsContext={EnumFrontendLayoutSetting.OrderPositionsTableLayout}
                    items={positions}
                    columns={positionColumns}
                    initialSorting={{
                        columnName: 'number',
                        direction: 'asc',
                    }}
                    name={'OrderPositions'}
                    editable={canEditPositions}
                    canCreate={canEditPositions}
                    onAdd={addPosition}
                    onDelete={deletePositions}
                    toolbarElements={toolbarElements}
                    renderSubComponent={(row) => (
                        <OrderPosition
                            key={row.id}
                            data={row}
                            meta={undefined}
                            canEdit={canEdit}
                            apiResourcePath={`${baseApiObjectPath}/positions`}
                            requestUpdateState={(u) => requestUpdatePosition(u, row.id)}
                            stateQueueDepth={stateQueueDepth}
                            refetchData={refetchData}
                            priceDisplay={taxationMode}
                            processState={processState}
                        />
                    )}
                />
            </FlexColumn>
        </FloatingCard>
    );
});
